/**
 * Chunk loading retry utility for Vite
 * Handles intermittent network failures when loading dynamic imports
 *
 * Usage: Replace lazy(() => import('./Component')) with lazyWithRetry(() => import('./Component'))
 */

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // ms

/**
 * Wraps a dynamic import with retry logic
 * @param {Function} importFn - Function that returns a dynamic import promise
 * @param {number} retries - Number of retries remaining
 * @returns {Promise} - The module or throws after all retries exhausted
 */
export function importWithRetry(importFn, retries = MAX_RETRIES) {
    return importFn().catch(error => {
        if (retries > 0) {
            console.warn(`Chunk loading failed, retrying... (${MAX_RETRIES - retries + 1}/${MAX_RETRIES})`);
            return new Promise(resolve => {
                setTimeout(() => {
                    resolve(importWithRetry(importFn, retries - 1));
                }, RETRY_DELAY);
            });
        }
        throw error;
    });
}

/**
 * Global handler for Vite chunk loading errors
 * Automatically reloads the page when a chunk fails to load after retries
 */
export function setupChunkErrorHandler() {
    // Handle preload errors (Vite-specific)
    window.addEventListener('vite:preloadError', event => {
        console.error('Vite preload error:', event.payload);
        // Prevent default error handling
        event.preventDefault();
        // Offer to reload
        if (window.confirm('A required resource failed to load. Reload the page?')) {
            window.location.reload();
        }
    });

    // Handle generic chunk loading errors
    window.addEventListener('error', event => {
        const isChunkError =
            event.message?.includes('Loading chunk') || event.message?.includes('Failed to fetch dynamically imported module') || event.message?.includes('Unable to preload CSS');

        if (isChunkError) {
            console.error('Chunk loading error detected:', event.message);
            // Could show a toast or modal here instead of confirm
            if (window.confirm('A required resource failed to load. Reload the page?')) {
                window.location.reload();
            }
        }
    });
}
